/**
 * How To Use — play the step GIFs one at a time, in order, on a loop.
 *
 * A GIF cannot be driven from script: there is no play(), no pause(), no seek,
 * and no event when it reaches its last frame. Two consequences shape all of
 * this.
 *
 * 1. "Stop the others" is done by covering each idle GIF with a <canvas>
 *    holding its first frame. The GIF underneath keeps looping in the decoder,
 *    but nothing of it is on screen, so the section reads as one clip at a time.
 * 2. "When it reaches the end" has to be told to us — hence the per-step
 *    Clip length setting. Nothing can measure it for us.
 *
 * Restarting from frame 1 is the other trick: re-assigning the same src does
 * nothing, so the src alternates between the plain URL and one carrying a fixed
 * marker. Two URLs, both cached after the first pass, and each swap restarts the
 * animation without re-downloading anything.
 */
(function () {
  var SECTION_SELECTOR = '.oddit-section-how-to-use';
  var MEDIA_SELECTOR = '[data-step-media]';
  var IMG_SELECTOR = '.oddit-section-how-to-use__card-img';
  var FROZEN_CLASS = 'is-frozen';
  var DEFAULT_DURATION = 3000;
  var DEFAULT_PLAYS = 2;

  var reducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  /* On mobile the cards are a horizontal scroller, so the step that is playing
     can easily be off-screen. Scroll it to the middle of the track as it starts.
     Only the track is scrolled — never scrollIntoView, which would also drag the
     page vertically. */
  function centerStep(step) {
    var card = step.wrap.closest ? step.wrap.closest('.oddit-section-how-to-use__card') : null;
    var track = card ? card.parentElement : null;
    if (!card || !track) return;

    /* Above the mobile breakpoint the track is a static row with nothing to
       scroll, and on any width a single screenful needs no help. */
    if (track.scrollWidth <= track.clientWidth + 1) return;

    var trackRect = track.getBoundingClientRect();
    var cardRect = card.getBoundingClientRect();
    var delta = cardRect.left - trackRect.left - (trackRect.width - cardRect.width) / 2;

    /* Measured from the rects rather than offsetLeft, which is relative to
       whatever the offset parent happens to be. The browser clamps the result,
       so the first and last cards simply rest against their edge. */
    try {
      track.scrollTo({
        left: track.scrollLeft + delta,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    } catch (e) {
      track.scrollLeft = track.scrollLeft + delta;
    }
  }

  function replaySrc(step) {
    /* Alternate between two stable URLs. A changing cache-buster would re-fetch
       the GIF on every cycle; this pair is cached after the first loop. */
    var base = step.src;
    if (step.usingMarker) {
      step.usingMarker = false;
      return base;
    }
    step.usingMarker = true;
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'oddit-replay=1';
  }

  function freeze(step) {
    var w = step.img.naturalWidth;
    var h = step.img.naturalHeight;
    if (!w || !h) return false;

    if (!step.canvas) {
      var canvas = document.createElement('canvas');
      canvas.className = 'oddit-section-how-to-use__card-freeze';
      canvas.setAttribute('aria-hidden', 'true');
      step.wrap.appendChild(canvas);
      step.canvas = canvas;
    }

    step.canvas.width = w;
    step.canvas.height = h;
    try {
      /* Display only — the canvas is tainted by the CDN image, so its pixels
         are never read back. */
      step.canvas.getContext('2d').drawImage(step.img, 0, 0, w, h);
    } catch (e) {
      return false;
    }

    step.wrap.classList.add(FROZEN_CLASS);
    return true;
  }

  function whenReady(img, cb) {
    if (img.complete && img.naturalWidth) {
      cb();
      return;
    }
    img.addEventListener('load', cb, { once: true });
  }

  function initSection(section) {
    var medias = section.querySelectorAll(MEDIA_SELECTOR);
    var steps = [];

    /* These GIFs loop on their own, so "play it twice" is simply holding the
       step for two clip lengths instead of one — no re-encoding, and the
       measured Clip length stays the honest length of the file. */
    var plays = parseInt(section.getAttribute('data-step-plays'), 10);
    if (!(plays > 0)) plays = DEFAULT_PLAYS;

    for (var i = 0; i < medias.length; i++) {
      var img = medias[i].querySelector(IMG_SELECTOR);
      if (!img) continue;
      var duration = parseInt(medias[i].getAttribute('data-step-duration'), 10);
      steps.push({
        wrap: medias[i],
        img: img,
        src: img.getAttribute('src') || '',
        clip: duration > 0 ? duration : DEFAULT_DURATION,
        duration: (duration > 0 ? duration : DEFAULT_DURATION) * plays,
        canvas: null,
        usingMarker: false
      });
    }

    /* One clip has nothing to hand over to. */
    if (steps.length < 2) return;

    var index = 0;
    var timer = null;
    var running = false;
    var touching = false;

    section.addEventListener('touchstart', function () { touching = true; }, { passive: true });
    section.addEventListener('touchend', function () { touching = false; }, { passive: true });
    section.addEventListener('touchcancel', function () { touching = false; }, { passive: true });

    function stop() {
      running = false;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    function playStep(i) {
      var step = steps[i];
      step.wrap.classList.remove(FROZEN_CLASS);
      step.img.src = replaySrc(step);

      /* Never yank the track out from under a finger mid-swipe. */
      if (!touching) centerStep(step);

      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        /* Hold the last thing the shopper saw rather than snapping to frame 1:
           freeze() captures whatever is on screen right now. */
        freeze(step);
        index = (i + 1) % steps.length;
        playStep(index);
      }, step.duration);
    }

    function start() {
      if (running) return;
      running = true;
      playStep(index);
    }

    /* Freeze everything up front, then let the first step run. Without this all
       three animate at once for as long as the section is on screen. */
    steps.forEach(function (step) {
      whenReady(step.img, function () {
        freeze(step);
      });
    });

    if (reducedMotion.matches) {
      /* Motion is the whole feature, so with it turned down the steps simply
         stay on their first frame. */
      return;
    }

    /* Only run while the section is actually in view — off-screen the sequence
       is invisible work, and it would also be mid-cycle by the time it is
       scrolled to. */
    if (typeof IntersectionObserver === 'function') {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              start();
            } else {
              stop();
            }
          });
        },
        { threshold: 0.2 }
      );
      io.observe(section);
    } else {
      start();
    }

    /* A background tab throttles timers, which would leave the sequence out of
       step with what is on screen when the shopper comes back. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else if (!reducedMotion.matches) {
        start();
      }
    });
  }

  function init() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    for (var i = 0; i < sections.length; i++) {
      initSection(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: the section is re-rendered wholesale on every change. */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector
      ? e.target.querySelector(SECTION_SELECTOR)
      : null;
    if (section) initSection(section);
  });
})();
